"use client";

import * as React from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";
import { motion } from "framer-motion";

import { cn } from "@/lib/utils";

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay asChild>
      <motion.div
        data-slot="sheet-overlay"
        className={cn("fixed inset-0 z-[100] bg-black/40 backdrop-blur-[4px]", className)}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        {...(props as any)}
      />
    </SheetPrimitive.Overlay>
  );
}

const getContentVariants = (side: "top" | "right" | "bottom" | "left") => {
  const baseTransition = {
    duration: 0.5,
    ease: [0.16, 1, 0.3, 1] as [number, number, number, number],
  };

  const closeTransition = {
    duration: 0.3,
    ease: [0.16, 1, 0.3, 1] as [number, number, number, number],
  };

  switch (side) {
    case "right":
      return {
        open: {
          x: 0,
          transition: baseTransition,
        },
        closed: {
          x: "100%",
          transition: closeTransition,
        },
      };
    case "left":
      return {
        open: {
          x: 0,
          transition: baseTransition,
        },
        closed: {
          x: "-100%",
          transition: closeTransition,
        },
      };
    case "top":
      return {
        open: {
          y: 0,
          transition: baseTransition,
        },
        closed: {
          y: "-100%",
          transition: closeTransition,
        },
      };
    case "bottom":
      return {
        open: {
          y: 0,
          transition: baseTransition,
        },
        closed: {
          y: "100%",
          transition: closeTransition,
        },
      };
  }
};

const getContentStyles = (side: "top" | "right" | "bottom" | "left") => {
  switch (side) {
    case "right":
      return "inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm";
    case "left":
      return "inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm";
    case "top":
      return "inset-x-0 top-0 h-auto border-b";
    case "bottom":
      return "inset-x-0 bottom-0 h-auto border-t";
  }
};

function SheetContent({
  className,
  children,
  side = "right",
  SheetOverlayClassName,
  hideCloseButton,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: "top" | "right" | "bottom" | "left";
  SheetOverlayClassName?: string;
  hideCloseButton?: boolean;
}) {
  const contentVariants = React.useMemo(
    () => getContentVariants(side),
    [side]
  );
  const contentStyles = React.useMemo(
    () => getContentStyles(side),
    [side]
  );

  return (
    <SheetPortal>
      <SheetOverlay className={SheetOverlayClassName} />
      <SheetPrimitive.Content asChild {...props}>
        <motion.div
          data-slot="sheet-content"
          className={cn(
            "bg-background fixed z-[100] flex flex-col gap-4 shadow-lg rounded-2xl",
            contentStyles,
            className
          )}
          variants={contentVariants}
          initial="closed"
          animate="open"
          exit="closed"
        >
          {/* Visually hidden title and description for accessibility */}
          <SheetPrimitive.Title className="sr-only">
            Sheet Content
          </SheetPrimitive.Title>
          <SheetPrimitive.Description className="sr-only">
            Sheet panel content
          </SheetPrimitive.Description>
          {children}
          {!hideCloseButton && (
            <SheetPrimitive.Close className="ring-offset-background focus:ring-ring data-[state=open]:bg-secondary absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none">
              <XIcon className="size-4" />
              <span className="sr-only">Close</span>
            </SheetPrimitive.Close>
          )}
        </motion.div>
      </SheetPrimitive.Content>
    </SheetPortal>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-header"
      className={cn("flex flex-col gap-1.5 p-4", className)}
      {...props}
    />
  );
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn("mt-auto flex flex-col gap-2 p-4", className)}
      {...props}
    />
  );
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn("text-foreground font-semibold", className)}
      {...props}
    />
  );
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
